import {
  AVAILABILITY,
  CATCH_LOG_ACTIVITIES,
  EVENT_KIND_ORDER,
  validateCatchLogFilters,
  validateCatchLogSlice,
  validateCatchLogSourceStatus,
} from "./catch-log-contract.js";
import { characterClassPresentation } from "./character-class-presentation.js";

const FILTER_EVENT = "fishy-catch-log-filter-change";
const QUERY_EVENT = "fishy-catch-log-query-change";
const IDENTITY_ANCHOR_EVENT = "fishy-catch-log-identity-anchor-request";
const TWELVE_HOURS_NS = 12n * 60n * 60n * 1_000_000_000n;
const SOURCE_STATUS_CLASS = Object.freeze({
  connecting: "status status-warning",
  catchingUp: "status status-warning",
  ready: "status status-success",
  warning: "status status-warning",
  failed: "status status-error",
});
const IDENTITY_CONFIDENCE = Object.freeze({
  userConfirmed: Object.freeze({
    label: "Assigned locally",
    className: "badge-secondary badge-soft",
    outlineClass: "border-2 border-primary border-solid",
    detail: "You assigned this local activity to this Character. The assignment stays on this installation and is not synchronized as source evidence.",
  }),
  sourceObserved: Object.freeze({
    label: "Exact identity",
    className: "badge-success badge-soft",
    outlineClass: "border-2 border-primary border-solid",
    detail: "This event carried the exact Character identity.",
  }),
  uninterruptedSession: Object.freeze({
    label: "Session identity",
    className: "badge-info badge-soft",
    outlineClass: "border-2 border-info border-dashed",
    detail: "An uninterrupted exact Character session covers this event.",
  }),
  sourceActorAssociation: Object.freeze({
    label: "Recovered identity",
    className: "badge-warning badge-soft",
    outlineClass: "border-2 border-warning border-dashed",
    detail: "An explicit recovery fact linked this anonymous session's local actor to an exact Character session.",
  }),
  unresolved: Object.freeze({
    label: "Unresolved identity",
    className: "badge-ghost",
    outlineClass: "border-2 border-base-content/25 border-dashed",
    detail: "No exact Character attribution is supported for this event after the latest capture boundary.",
  }),
});

const viewTemplate = document.createElement("template");
viewTemplate.innerHTML = `
  <header class="page-title-shell not-prose">
    <div class="page-title-stack">
      <h1 class="page-title-heading">Activity log</h1>
    </div>
  </header>

  <section class="card card-border overflow-hidden bg-base-100 shadow-sm" aria-labelledby="fishy-character-heading">
    <div class="border-b border-base-300 px-4 py-3 sm:px-5">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.16em] text-primary">Activity</p>
          <h2 id="fishy-character-heading" class="mt-1 text-lg font-bold">Choose a Character</h2>
        </div>
        <div class="flex items-center gap-2 text-xs text-base-content/60" role="status" aria-live="polite">
          <span class="status status-warning" data-catch-log-source-dot aria-hidden="true"></span>
          <span data-catch-log-source-label>Connecting</span>
        </div>
      </div>
    </div>
    <div class="relative">
      <div id="fishy-character-rail" class="flex snap-x gap-3 overflow-x-auto p-4 sm:p-5" role="tablist" aria-label="Characters and unresolved activity" aria-busy="true">
        <span class="loading loading-spinner loading-sm self-center" aria-label="Loading Characters"></span><div class="skeleton h-32 w-28 shrink-0 rounded-box"></div><div class="skeleton h-32 w-28 shrink-0 rounded-box"></div><div class="skeleton h-32 w-28 shrink-0 rounded-box"></div>
      </div>
      <div hidden class="border-t border-base-300 px-4 py-2 sm:px-5" data-historical-character-control></div>
      <div hidden class="border-t border-base-300 px-4 py-2 sm:px-5" data-inactive-character-control></div>
    </div>
  </section>

  <div hidden>
    <select id="fishy-catch-log-character-filter" aria-label="Selected Character"
            data-bind="_catch_log_filter.character"
            data-on:change="$_catch_log_filter.character = el.value; $_catch_log_filter.family = 'all'; el.closest('#fishy-catch-log-root').dispatchEvent(new CustomEvent('${FILTER_EVENT}'))">
      <option value="all">All Characters</option>
    </select>
    <select id="fishy-catch-log-family-filter" aria-label="Selected Family" data-bind="_catch_log_filter.family"><option value="all">All Families</option></select>
    <select id="fishy-catch-log-activity-filter" aria-label="Selected activity" data-bind="_catch_log_filter.activity"><option value="fishing">Fishing</option></select>
  </div>

  <section id="fishy-character-activity" class="grid gap-4" aria-labelledby="fishy-selected-character-heading">
    <div class="flex min-w-0 flex-wrap items-end justify-between gap-3 px-1">
      <div class="min-w-0">
        <p class="text-xs text-base-content/55" data-selected-character-family>All retained Families</p>
        <h2 id="fishy-selected-character-heading" class="truncate text-xl font-bold" data-selected-character-name>All Characters</h2>
      </div>
      <div id="fishy-activity-tabs" class="tabs tabs-box" role="tablist" aria-label="Activity type">
        <button type="button" class="tab tab-active gap-2" role="tab" aria-selected="true" data-activity="fishing">
          Fishing
        </button>
      </div>
    </div>

    <div class="hidden rounded-box border border-dashed border-base-300 bg-base-200/25 p-8 text-center" data-no-character-activity>
      <h3 class="font-semibold">No supported activity yet</h3>
      <p class="mx-auto mt-1 max-w-2xl text-sm leading-5 text-base-content/60">This selection has identity or session evidence, but no fishing activity.</p>
    </div>
    <div class="card card-border bg-base-100 shadow-sm" data-fishing-workspace>
      <div class="card-body gap-4 p-4 sm:p-5">
        <div class="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p class="text-xs font-semibold uppercase tracking-[0.14em] text-primary">Fishing</p>
            <h3 class="mt-1 text-lg font-bold">Activity workspace</h3>
          </div>
          <div class="tabs tabs-border" role="tablist" aria-label="Fishing data lens">
            <button type="button" class="tab tab-active" role="tab" aria-selected="true" data-lens="overview">Overview</button>
            <button type="button" class="tab" role="tab" aria-selected="false" data-lens="log">Event log</button>
            <button type="button" class="tab" role="tab" aria-selected="false" data-lens="rates">Rates</button>
            <button type="button" class="tab" role="tab" aria-selected="false" data-lens="groups">Group discovery</button>
          </div>
        </div>

        <div id="fishy-fishing-overview" data-lens-panel="overview" role="tabpanel" class="grid gap-4"></div>

        <div id="fishy-fishing-log" data-lens-panel="log" role="tabpanel" class="hidden grid gap-4">
          <div id="fishy-catch-log-filters" class="grid gap-3 rounded-box border border-base-300 bg-base-200/35 p-3 sm:grid-cols-2 lg:grid-cols-4" aria-label="Fishing event filters" aria-busy="true">
            <div class="fieldset min-w-0">
              <label class="fieldset-legend" for="fishy-catch-log-event-types-filter">Event types</label>
              <select id="fishy-catch-log-event-types-filter" class="select h-auto min-w-0 w-full py-1 select-sm" multiple size="4" data-bind="_catch_log_filter.event_types" data-on:change="el.closest('#fishy-catch-log-root').dispatchEvent(new CustomEvent('${FILTER_EVENT}'))">
                <option value="fishing_catch_recorded" selected>Fishing catches</option>
                <option value="character_session_started" selected>Session starts</option>
                <option value="character_session_observed" selected>Session details</option>
                <option value="character_session_observation_ended" selected>Session ends</option>
              </select>
            </div>
            <div class="fieldset min-w-0">
              <label class="fieldset-legend" for="fishy-catch-log-location-filter">Fishing zone</label>
              <select id="fishy-catch-log-location-filter" class="select select-sm min-w-0 w-full" data-bind="_catch_log_filter.location" data-on:change="$_catch_log_filter.location = el.value; el.closest('#fishy-catch-log-root').dispatchEvent(new CustomEvent('${FILTER_EVENT}'))"><option value="all">All locations</option></select>
              <p class="label whitespace-normal">Only catch-local evidence proves a fishing zone.</p>
            </div>
            <div class="fieldset min-w-0">
              <label class="fieldset-legend" for="fishy-catch-log-timeframe-filter">Timeframe</label>
              <select id="fishy-catch-log-timeframe-filter" class="select select-sm min-w-0 w-full" data-bind="_catch_log_filter.timeframe" data-on:change="$_catch_log_filter.timeframe = el.value; el.closest('#fishy-catch-log-root').dispatchEvent(new CustomEvent('${FILTER_EVENT}'))">
                <option value="1h">Last hour</option><option value="6h">Last 6 hours</option><option value="12h" selected>Last 12 hours</option><option value="24h">Last 24 hours</option><option value="7d">Last 7 days</option><option value="all">All retained</option>
              </select>
            </div>
            <div class="fieldset min-w-0">
              <label class="fieldset-legend" for="fishy-catch-log-limit-filter">Rows</label>
              <select id="fishy-catch-log-limit-filter" class="select select-sm min-w-0 w-full" data-bind="_catch_log_filter.limit" data-on:change="$_catch_log_filter.limit = el.value; el.closest('#fishy-catch-log-root').dispatchEvent(new CustomEvent('${FILTER_EVENT}'))">
                <option value="25">Newest 25</option><option value="50">Newest 50</option><option value="100" selected>Newest 100</option><option value="200">Newest 200</option><option value="400">Newest 400</option>
              </select>
            </div>
          </div>
          <div id="fishy-catch-log-stream"></div>
        </div>

        <div id="fishy-catch-log-rates" data-lens-panel="rates" role="tabpanel" class="hidden"></div>
        <div id="fishy-catch-log-groups" data-lens-panel="groups" role="tabpanel" class="hidden"></div>
      </div>
    </div>
  </section>

  <script id="fishy-catch-log-slice-data" type="application/json">{"schemaVersion":1,"projectionVersion":0,"rows":[],"statistics":{"eventCount":0,"catchCount":0,"outcomeCount":0,"eligibleCatchCount":0,"provenOutcomeCount":0,"excludedCompositionCatchCount":0,"omittedUnprovenOutcomeRows":0,"firstSourceObservedAtNs":null,"lastSourceObservedAtNs":null},"outcomes":[],"cooccurrences":[],"filters":{"families":[],"characters":[],"locations":[]},"candidateRows":0}</script>
  <script id="fishy-catch-log-source-data" type="application/json">{"schemaVersion":1,"state":"connecting","label":"Connecting","detail":"Opening the selected activity source."}</script>
`;

function element(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = String(text);
  return node;
}

function emptyCard(heading, detail) {
  const root = element("div", "rounded-box border border-dashed border-base-300 bg-base-200/25 p-8 text-center");
  root.append(
    element("h4", "font-semibold", heading),
    element("p", "mx-auto mt-1 max-w-2xl text-sm leading-5 text-base-content/60", detail),
  );
  return root;
}

function formatObservedTime(value) {
  return new Date(Number(BigInt(value) / 1_000_000n)).toLocaleString();
}

function formatDuration(firstNs, lastNs) {
  if (firstNs === null || lastNs === null) return "No observed span";
  const seconds = Math.max(0, Number((BigInt(lastNs) - BigInt(firstNs)) / 1_000_000_000n));
  if (seconds < 60) return `${seconds} sec`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min`;
  if (seconds < 86400) return `${(seconds / 3600).toFixed(1)} hr`;
  return `${(seconds / 86400).toFixed(1)} days`;
}

function percentage(part, total) {
  if (!total) return "—";
  return `${((part / total) * 100).toFixed(1)}%`;
}

function replaceOptions(select, options, allLabel) {
  const selected = select.value;
  const all = element("option", "", allLabel);
  all.value = "all";
  const entries = options.map((entry) => {
    const option = element("option", "", `${entry.label} (${entry.retainedEvents})`);
    option.value = entry.value;
    return option;
  });
  select.replaceChildren(all, ...entries);
  select.value = entries.some((option) => option.value === selected) ? selected : "all";
}

function setSelectValue(select, value) {
  if (!select || select.value === value) return;
  select.value = value;
  select.dispatchEvent(new Event("input", { bubbles: true }));
  select.dispatchEvent(new Event("change", { bubbles: true }));
}

function characterInitials(label) {
  return label.split(/\s+/u).slice(0, 2).map((part) => part[0] ?? "").join("").toUpperCase() || "?";
}

function activitySummary(character, activity) {
  return character.activitySummaries.find((summary) => summary.activity === activity) ?? null;
}

function latestActivityTime(character) {
  return character.activitySummaries.reduce((latest, summary) => {
    const observedAt = BigInt(summary.latestSourceObservedAtNs);
    return latest === null || observedAt > latest ? observedAt : latest;
  }, null);
}

function hasRecentActivity(character, nowNs) {
  const earliest = nowNs - TWELVE_HOURS_NS;
  return character.activitySummaries.some(
    (summary) => BigInt(summary.latestSourceObservedAtNs) >= earliest,
  );
}

function combinedActivitySummaries(characters) {
  const combined = new Map();
  for (const character of characters) {
    for (const summary of character.activitySummaries) {
      const current = combined.get(summary.activity);
      combined.set(summary.activity, {
        activity: summary.activity,
        retainedEvents: (current?.retainedEvents ?? 0) + summary.retainedEvents,
        latestSourceObservedAtNs: current === undefined
          || BigInt(summary.latestSourceObservedAtNs) > BigInt(current.latestSourceObservedAtNs)
          ? summary.latestSourceObservedAtNs
          : current.latestSourceObservedAtNs,
      });
    }
  }
  return [...combined.values()].sort(
    (left, right) => CATCH_LOG_ACTIVITIES.indexOf(left.activity)
      - CATCH_LOG_ACTIVITIES.indexOf(right.activity),
  );
}

function spriteIcon(name, className) {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("class", `fishy-icon ${className}`);
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("aria-hidden", "true");
  const use = document.createElementNS("http://www.w3.org/2000/svg", "use");
  use.setAttribute("width", "100%");
  use.setAttribute("height", "100%");
  use.setAttribute("href", `/img/icons.svg#fishy-${name}`);
  svg.append(use);
  return svg;
}

function portrait(character, characterIndex, localPortrait, { compact = false, confidence = null, showOnline = true } = {}) {
  const root = element("div", "avatar relative mx-auto");
  const frameSize = compact ? "h-12 w-12" : "h-20 w-20";
  const outline = confidence?.outlineClass ?? "border border-base-300 border-solid";
  const frame = element("div", `relative ${frameSize} overflow-hidden rounded-box bg-base-300 ${outline}`);
  const fallbackLabel = character.identityState === "unresolvedActivity"
    ? "?"
    : characterInitials(character.label);
  const fallback = element("div", "grid h-full w-full place-items-center bg-base-300 text-xl font-black text-base-content/70");
  if (character.identityState === "all") {
    fallback.append(spriteIcon("catch-log-all-characters", "size-9"));
  } else {
    fallback.textContent = fallbackLabel;
  }
  frame.append(fallback);
  const classPortrait = characterClassPresentation(character.characterClassKey);
  const candidates = [localPortrait, classPortrait?.portraitUrl].filter(Boolean);
  if (candidates.length) {
    const image = element("img");
    image.className = "absolute inset-0 h-full w-full object-cover";
    image.width = compact ? 48 : 80;
    image.height = compact ? 48 : 80;
    image.src = candidates.shift();
    image.alt = "";
    image.loading = characterIndex < 5 ? "eager" : "lazy";
    image.fetchPriority = characterIndex < 5 ? "high" : "auto";
    image.addEventListener("load", () => {
      fallback.remove();
    }, { once: true });
    image.addEventListener("error", () => {
      const next = candidates.shift();
      if (next) image.src = next;
      else image.remove();
    });
    frame.append(image);
  }
  root.append(frame);
  if (showOnline && character.currentlyInferred) {
    const online = element(
      "span",
      "absolute -bottom-0.5 -right-0.5 z-10 size-3 rounded-full bg-success ring-2 ring-base-100",
    );
    online.dataset.characterOnline = "";
    online.setAttribute("role", "img");
    online.setAttribute("aria-label", "Online");
    online.dataset.fishyTooltip = "Online: the Fishing Logger currently infers this Character on a verified local game client.";
    root.append(online);
  }
  return root;
}

function eventCharacter(row, filters) {
  return filters.characters.find((character) => character.value === row.characterFilterKey) ?? {
    value: row.characterFilterKey,
    label: row.characterLabel,
    identityState: row.identityConfidence === "unresolved" ? "unresolvedActivity" : "exactCharacter",
    characterClassKey: null,
  };
}

function eventIdentity(row, filters, rowIndex, localPortrait) {
  const confidence = IDENTITY_CONFIDENCE[row.identityConfidence];
  const character = eventCharacter(row, filters);
  const root = element("div", "grid w-[4.75rem] shrink-0 justify-items-center gap-1 text-center sm:w-[5.5rem]");
  root.dataset.eventIdentity = row.characterFilterKey;
  root.dataset.identityConfidence = row.identityConfidence;
  root.setAttribute("role", "group");
  root.setAttribute("aria-label", `${row.characterLabel}: ${confidence.label}`);
  root.dataset.fishyTooltip = confidence.detail;
  root.append(portrait(character, rowIndex, localPortrait, { compact: true, confidence, showOnline: false }));

  const name = element("span", "max-w-full truncate text-xs font-semibold leading-tight", row.characterLabel);
  name.dataset.eventCharacterLabel = "";
  name.dataset.fishyTooltip = row.characterLabel;
  name.tabIndex = 0;
  const family = element("span", "max-w-full truncate text-[0.65rem] leading-tight text-base-content/50", row.familyLabel);
  family.dataset.fishyTooltip = row.familyLabel;
  family.tabIndex = 0;
  root.append(name, family);
  return root;
}

function eventMeta(row) {
  const root = element("div", "flex min-w-0 flex-wrap items-center gap-2 px-1 text-xs text-base-content/55");
  root.append(element("time", "tabular-nums", formatObservedTime(row.sourceObservedAtNs)));
  const availability = AVAILABILITY[row.availability];
  root.append(element("span", `badge badge-sm ${availability.className}`, availability.label));
  return root;
}

function eventLocation(row) {
  if (row.kind !== "fishingCatch") return null;
  const root = element("span", "inline-flex min-w-0 items-center gap-1.5 text-xs text-base-content/60");
  const rgb = /^location-zone-([0-9a-f]{6})$/u.exec(row.locationFilterKey ?? "")?.[1] ?? null;
  if (rgb) {
    const dot = element("span", "size-2.5 shrink-0 rounded-full ring-1 ring-base-content/20");
    dot.style.backgroundColor = `#${rgb}`;
    dot.dataset.fishyTooltip = `Zone color #${rgb.toUpperCase()}`;
    dot.setAttribute("role", "img");
    dot.setAttribute("aria-label", `Zone color #${rgb.toUpperCase()}`);
    root.append(dot);
  }
  root.append(element("span", "min-w-0 truncate", row.locationLabel ?? "Fishing zone unavailable"));
  return root;
}

const ITEM_GRADE_CLASSES = Object.freeze({
  unknown: "fishy-item-grade-unknown",
  white: "fishy-item-grade-white",
  green: "fishy-item-grade-green",
  blue: "fishy-item-grade-blue",
  yellow: "fishy-item-grade-yellow",
  red: "fishy-item-grade-red",
});

function resolveCdnUrl(path) {
  const resolver = globalThis.window?.__fishystuffResolveCdnUrl;
  if (typeof resolver !== "function") return path;
  const resolved = resolver(path);
  return typeof resolved === "string" && resolved.length > 0 ? resolved : path;
}

function itemOutcome(outcome) {
  const gradeClass = ITEM_GRADE_CLASSES[outcome.gradeTone] ?? ITEM_GRADE_CLASSES.unknown;
  const root = element("div", "flex min-w-0 items-center gap-2");
  const frame = element("span", `fishy-item-icon-frame is-sm shrink-0 ${gradeClass}`);
  frame.setAttribute("aria-hidden", "true");
  if (outcome.iconPath) {
    const image = element("img", "fishy-item-icon");
    image.src = resolveCdnUrl(outcome.iconPath);
    image.alt = "";
    image.loading = "lazy";
    image.referrerPolicy = "no-referrer";
    image.addEventListener("error", () => {
      image.remove();
      const fallback = element("span", `fishy-item-icon-fallback ${gradeClass}`, "?");
      frame.append(fallback);
    }, { once: true });
    frame.append(image);
  } else {
    frame.append(element("span", `fishy-item-icon-fallback ${gradeClass}`, "?"));
  }
  root.append(frame, element("span", "fishy-domain-label min-w-0 truncate font-semibold", outcome.label));
  return root;
}

function eventBubble(row) {
  const bubble = element("div", "min-w-0 rounded-box bg-base-200/45 px-3 py-2.5");
  const content = element("div", "min-w-0");
  if (row.kind === "fishingCatch" && row.outcomes?.length) {
    const outcomes = element("div", "grid min-w-0 gap-1");
    row.outcomes.forEach((outcome) => outcomes.append(itemOutcome(outcome)));
    content.append(outcomes);
  } else {
    content.append(element("p", "min-w-0 font-semibold", row.summary));
  }
  bubble.append(content);
  const location = eventLocation(row);
  if (location) {
    const locationRow = element("div", "mt-2");
    locationRow.append(location);
    bubble.append(locationRow);
  }
  if (row.sourceSessionIdentity?.state === "exactCharacter") {
    bubble.append(element(
      "p",
      "mt-2 break-words font-mono text-xs text-base-content/60",
      `Exact source scope · Region ${row.sourceSessionIdentity.serviceRegion} · Family ID ${row.sourceSessionIdentity.familyId} · Character ID ${row.sourceSessionIdentity.characterId}`,
    ));
  } else if (row.sourceSessionIdentity?.state === "unresolved") {
    bubble.append(element(
      "p",
      "mt-2 text-xs text-base-content/50",
      "Source session scope · unresolved local client",
    ));
  }
  return bubble;
}

function selectedCharacterData(data, selectedValue) {
  if (selectedValue === "all") return data;
  const rows = data.rows.filter((row) => row.characterFilterKey === selectedValue);
  const catches = rows.filter((row) => row.kind === "fishingCatch");
  const outcomeLabels = new Set(rows.flatMap((row) => row.outcomes.map((outcome) => outcome.label)));
  const outcomes = data.outcomes
    .filter((outcome) => outcomeLabels.has(outcome.label))
    .map((outcome) => ({ ...outcome }));
  const cooccurrences = data.cooccurrences
    .filter((pair) => outcomeLabels.has(pair.leftLabel) && outcomeLabels.has(pair.rightLabel))
    .map((pair) => ({ ...pair }));
  const first = rows.at(-1)?.sourceObservedAtNs ?? null;
  const last = rows[0]?.sourceObservedAtNs ?? null;
  const provenOutcomeCount = rows.reduce((total, row) => total + row.outcomes.length, 0);
  const eligibleCatchCount = catches.filter((row) => row.outcomes.length > 0).length;
  return {
    ...data,
    rows,
    outcomes,
    cooccurrences,
    candidateRows: Math.min(data.candidateRows, rows.length * 4),
    statistics: {
      ...data.statistics,
      eventCount: rows.length,
      catchCount: catches.length,
      outcomeCount: provenOutcomeCount,
      eligibleCatchCount,
      provenOutcomeCount,
      excludedCompositionCatchCount: catches.length - eligibleCatchCount,
      omittedUnprovenOutcomeRows: 0,
      firstSourceObservedAtNs: first,
      lastSourceObservedAtNs: last,
    },
  };
}

function stat(title, value, detail) {
  const root = element("div", "stat px-4 py-3");
  root.append(
    element("div", "stat-title text-xs", title),
    element("div", "stat-value text-2xl tabular-nums", value),
    element("div", "stat-desc", detail),
  );
  return root;
}

function listCard(entries, emptyHeading, emptyDetail, renderEntry) {
  if (!entries.length) return emptyCard(emptyHeading, emptyDetail);
  const card = element("div", "overflow-hidden rounded-box border border-base-300");
  const list = element("ol", "list divide-y divide-base-300/70");
  entries.forEach((entry, index) => list.append(renderEntry(entry, index)));
  card.append(list);
  return card;
}

export class FishyCatchLogView extends HTMLElement {
  #abort = null;
  #carrierObserver = null;
  #model = null;
  #sourceStatus = null;
  #lens = "overview";
  #localPortraits = new Map();
  #showHistoricalCharacters = false;
  #showInactiveCharacters = false;
  #pointerSelectionActive = false;
  #pointerHoverActive = false;
  #presentationPending = false;

  set localPortraits(value) {
    this.#localPortraits = value instanceof Map ? new Map(value) : new Map();
    if (this.#model) this.#renderPresentation();
  }

  connectedCallback() {
    if (this.#abort) return;
    if (!this.querySelector("#fishy-character-rail")) this.append(viewTemplate.content.cloneNode(true));
    this.classList.add("not-prose", "grid", "gap-4");
    this.#abort = new AbortController();
    const signal = this.#abort.signal;
    this.addEventListener("pointerdown", () => {
      this.#pointerSelectionActive = true;
    }, { signal });
    // Read-model notifications are event-driven and can arrive while the
    // pointer is over the Character rail. Replacing the rail in that window
    // invalidates the hovered button before its click is delivered. Keep the
    // current presentation stable for the whole pointer interaction; the
    // queued model is rendered as soon as the pointer leaves the rail.
    this.addEventListener("pointerover", (event) => {
      if (event.target.closest?.("#fishy-character-rail")) {
        this.#pointerHoverActive = true;
      }
    }, { signal });
    this.addEventListener("pointerout", (event) => {
      const rail = event.target.closest?.("#fishy-character-rail");
      const related = event.relatedTarget;
      if (rail && !(related instanceof Node && rail.contains(related))) {
        this.#pointerHoverActive = false;
        this.#flushPendingPresentation();
      }
    }, { signal });
    const finishPointerSelection = () => {
      this.#pointerSelectionActive = false;
      this.#flushPendingPresentation();
    };
    document.addEventListener("pointerup", finishPointerSelection, { signal });
    document.addEventListener("pointercancel", finishPointerSelection, { signal });
    window.addEventListener("blur", finishPointerSelection, { signal });
    document.addEventListener("selectionchange", () => {
      this.#flushPendingPresentation();
    }, { signal });
    this.addEventListener("focusout", () => {
      queueMicrotask(() => this.#flushPendingPresentation());
    }, { signal });
    this.addEventListener("click", (event) => {
      const historicalToggle = event.target.closest?.("[data-toggle-historical-characters]");
      if (historicalToggle) {
        this.#showHistoricalCharacters = !this.#showHistoricalCharacters;
        this.#renderCharacters(validateCatchLogSlice(this.#model));
        return;
      }
      const inactiveToggle = event.target.closest?.("[data-toggle-inactive-characters]");
      if (inactiveToggle) {
        this.#showInactiveCharacters = !this.#showInactiveCharacters;
        this.#renderCharacters(validateCatchLogSlice(this.#model));
        return;
      }
      const identityAnchor = event.target.closest?.("[data-identity-anchor-sequence]");
      if (identityAnchor && this.getAttribute("source") === "desktop") {
        this.dispatchEvent(new CustomEvent(IDENTITY_ANCHOR_EVENT, {
          bubbles: true,
          detail: Object.freeze({
            localSourceSequence: identityAnchor.dataset.identityAnchorSequence,
            currentCharacterLabel: identityAnchor.dataset.identityAnchorCharacter,
          }),
        }));
        return;
      }
      const button = event.target.closest?.("[data-character-value]");
      if (button) {
        setSelectValue(
          this.querySelector("#fishy-catch-log-character-filter"),
          button.dataset.characterValue,
        );
        // Update only the selection state in place. The changed query still
        // goes to the authoritative source; replacing the whole rail here
        // would invalidate the button that is delivering this click.
        this.#renderCharacterSelection(button.dataset.characterValue);
      }
    }, { signal });
    this.querySelectorAll("[data-lens]").forEach((tab) => tab.addEventListener("click", () => {
      this.#lens = tab.dataset.lens;
      this.#renderLensSelection();
    }, { signal }));
    this.addEventListener("change", (event) => {
      if (event.target instanceof HTMLSelectElement && this.getAttribute("source") === "desktop") {
        this.dispatchEvent(new CustomEvent(QUERY_EVENT, { bubbles: true, detail: this.query() }));
      }
    }, { signal });
    if (this.getAttribute("source") !== "desktop") {
      this.#carrierObserver = new MutationObserver((records) => {
        const changed = records.some((record) => {
          if (["fishy-catch-log-slice-data", "fishy-catch-log-source-data"].includes(record.target.id)) return true;
          return [...record.addedNodes].some((node) => node instanceof Element && (
            ["fishy-catch-log-slice-data", "fishy-catch-log-source-data"].includes(node.id)
            || node.querySelector?.("#fishy-catch-log-slice-data, #fishy-catch-log-source-data")
          ));
        });
        if (changed) this.#readCarriers();
      });
      this.#carrierObserver.observe(this, { childList: true, subtree: true, characterData: true });
      this.#readCarriers();
    }
    this.#renderPresentation();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    this.#carrierObserver?.disconnect();
    this.#carrierObserver = null;
    this.#pointerSelectionActive = false;
    this.#pointerHoverActive = false;
    this.#presentationPending = false;
  }

  set model(value) {
    this.#model = validateCatchLogSlice(value);
    if (this.getAttribute("source") === "desktop") {
      const hasLocal = this.#model.rows.some((row) => ["localOnly", "localAndOnline"].includes(row.availability));
      const hasOnline = this.#model.rows.some((row) => ["online", "onlineCache", "localAndOnline"].includes(row.availability));
      const detail = hasLocal && hasOnline
        ? "Showing local events together with the last verified account activity cache."
        : hasOnline
          ? "Showing rows from the last verified account activity cache."
          : hasLocal
            ? "Showing events from this installation’s local store."
            : "No activity rows are selected; local fishing logging remains available.";
      this.#sourceStatus = validateCatchLogSourceStatus({
        schemaVersion: 1,
        state: "ready",
        label: "Local activity ready",
        detail,
      });
    }
    this.#renderPresentation();
  }

  get model() { return this.#model; }

  query() {
    const eventKinds = [...this.querySelector("#fishy-catch-log-event-types-filter").selectedOptions]
      .map((option) => ({
        fishing_catch_recorded: "fishingCatch",
        character_session_started: "characterSessionStarted",
        character_session_observed: "characterSessionObserved",
        character_session_observation_ended: "characterSessionEnded",
      })[option.value])
      .filter(Boolean)
      .sort((left, right) => EVENT_KIND_ORDER.indexOf(left) - EVENT_KIND_ORDER.indexOf(right));
    if (!eventKinds.length) throw new Error("Select at least one event type.");
    const timeframe = this.querySelector("#fishy-catch-log-timeframe-filter").value;
    const hours = { "1h": 1, "6h": 6, "12h": 12, "24h": 24, "7d": 168 }[timeframe] ?? null;
    const valueOrNull = (selector) => {
      const value = this.querySelector(selector).value;
      return value === "all" ? null : value;
    };
    return Object.freeze({
      schemaVersion: 1,
      familyFilterKey: null,
      characterFilterKey: valueOrNull("#fishy-catch-log-character-filter"),
      eventKinds,
      locationFilterKey: valueOrNull("#fishy-catch-log-location-filter"),
      earliestSourceObservedAtNs: hours === null
        ? null
        : (BigInt(Date.now()) * 1_000_000n - BigInt(hours) * 3_600_000_000_000n).toString(),
      limit: Number(this.querySelector("#fishy-catch-log-limit-filter").value),
    });
  }

  #readCarriers() {
    try {
      const slice = this.querySelector("#fishy-catch-log-slice-data");
      const status = this.querySelector("#fishy-catch-log-source-data");
      if (slice) this.#model = validateCatchLogSlice(JSON.parse(slice.textContent));
      if (status) this.#sourceStatus = validateCatchLogSourceStatus(JSON.parse(status.textContent));
      this.#renderPresentation();
    } catch (_error) {
      this.#sourceStatus = {
        schemaVersion: 1,
        state: "failed",
        label: "Activity unavailable",
        detail: "The activity source returned an incompatible data contract.",
      };
      this.#renderPresentation();
    }
  }

  #interactionProtectsPresentation() {
    if (this.#pointerSelectionActive) return true;
    if (this.#pointerHoverActive) return true;
    const selection = document.getSelection?.();
    if (selection && !selection.isCollapsed && (
      this.contains(selection.anchorNode) || this.contains(selection.focusNode)
    )) return true;
    const active = document.activeElement;
    return active instanceof Element
      && this.contains(active)
      && active.matches("button, input, textarea, select, [contenteditable='true']");
  }

  #renderPresentation() {
    if (!this.isConnected) return;
    if (this.#interactionProtectsPresentation()) {
      this.#presentationPending = true;
      return;
    }
    this.#presentationPending = false;
    this.#renderModel();
    this.#renderSourceStatus();
  }

  #flushPendingPresentation() {
    if (!this.#presentationPending || this.#interactionProtectsPresentation()) return;
    this.#renderPresentation();
  }

  #renderModel() {
    if (!this.#model) return;
    const data = validateCatchLogSlice(this.#model);
    this.#renderFilters(data.filters);
    this.#renderCharacters(data);
    const selectedData = selectedCharacterData(
      data,
      this.querySelector("#fishy-catch-log-character-filter").value,
    );
    this.#renderOverview(selectedData);
    this.#renderStream(selectedData);
    this.#renderRates(selectedData);
    this.#renderGroups(selectedData);
    this.#renderLensSelection();
  }

  #renderFilters(filters) {
    validateCatchLogFilters(filters);
    replaceOptions(this.querySelector("#fishy-catch-log-family-filter"), filters.families, "All Families");
    replaceOptions(this.querySelector("#fishy-catch-log-character-filter"), filters.characters, "All Characters");
    replaceOptions(this.querySelector("#fishy-catch-log-location-filter"), filters.locations, "All locations");
    this.querySelector("#fishy-catch-log-filters")?.setAttribute("aria-busy", "false");
  }

  #renderCharacters(data) {
    const selectedValue = this.querySelector("#fishy-catch-log-character-filter").value;
    const familyByValue = new Map(data.filters.families.map((entry) => [entry.value, entry]));
    const nowNs = BigInt(Date.now()) * 1_000_000n;
    const orderedCharacters = [...data.filters.characters].sort((left, right) => {
      if (left.currentlyInferred !== right.currentlyInferred) {
        return left.currentlyInferred ? -1 : 1;
      }
      const leftLatest = latestActivityTime(left);
      const rightLatest = latestActivityTime(right);
      if (leftLatest !== null || rightLatest !== null) {
        if (leftLatest === null) return 1;
        if (rightLatest === null) return -1;
        const latestOrder = rightLatest - leftLatest;
        if (latestOrder !== 0n) return latestOrder > 0n ? 1 : -1;
      }
      return left.label.localeCompare(right.label);
    });
    const historicalCharacters = orderedCharacters.filter(
      (character) => character.retainedEvents > 0 && !hasRecentActivity(character, nowNs),
    );
    const visibleCharacters = orderedCharacters.filter((character) => (
      (this.#showInactiveCharacters
        || character.retainedEvents > 0
        || character.value === selectedValue)
      && (this.#showHistoricalCharacters
        || hasRecentActivity(character, nowNs)
        || character.currentlyInferred
        || character.value === selectedValue)
    ));
    const inactiveCharacters = orderedCharacters.filter((character) => character.retainedEvents === 0);
    const allActivitySummaries = combinedActivitySummaries(orderedCharacters);
    const characters = [{
      value: "all", label: "All Characters", familyValue: "", retainedEvents: data.filters.characters.reduce((sum, entry) => sum + entry.retainedEvents, 0), identityState: "all", identityConfidence: null, characterClassKey: null, currentlyInferred: false, activitySummaries: allActivitySummaries,
    }, ...visibleCharacters];
    const rail = element("div", "flex snap-x gap-3 overflow-x-auto p-4 sm:p-5");
    rail.id = "fishy-character-rail";
    rail.setAttribute("role", "tablist");
    rail.setAttribute("aria-label", "Characters and unresolved activity");
    rail.setAttribute("aria-busy", "false");
    const exactCharacterCount = data.filters.characters.filter((entry) => entry.identityState === "exactCharacter").length;
    const hasUnresolved = data.filters.characters.some((entry) => entry.identityState === "unresolvedActivity");
    const allCharactersSummary = `${exactCharacterCount} Character${exactCharacterCount === 1 ? "" : "s"}${hasUnresolved ? " · unresolved" : ""}`;
    for (const [characterIndex, character] of characters.entries()) {
      const selected = character.value === selectedValue;
      const button = element("button", `btn h-auto w-28 shrink-0 snap-start flex-col gap-2 px-2 py-3 ${selected ? "btn-primary" : "btn-ghost border border-base-300"}`);
      button.type = "button";
      button.dataset.characterValue = character.value;
      button.setAttribute("role", "tab");
      button.setAttribute("aria-selected", String(selected));
      button.append(portrait(character, characterIndex, this.#localPortraits.get(character.value)));
      const copy = element("span", "grid min-w-0 gap-0.5 text-center normal-case");
      let subtitle = familyByValue.get(character.familyValue)?.label ?? "Family unavailable";
      if (character.value === "all") subtitle = allCharactersSummary;
      if (character.identityState === "unresolvedActivity") {
        subtitle = hasRecentActivity(character, nowNs)
          ? "Active unresolved capture"
          : "Unresolved capture history";
      }
      copy.append(
        element("span", "max-w-full truncate text-xs font-semibold", character.label),
        element("span", `text-[0.65rem] ${selected ? "text-primary-content/75" : "text-base-content/50"}`, subtitle),
      );
      button.append(copy);
      const markers = element("span", "flex min-h-4 items-center justify-center gap-1", "");
      const fishing = activitySummary(character, "fishing");
      if (fishing) {
        const recent = BigInt(fishing.latestSourceObservedAtNs) >= nowNs - TWELVE_HOURS_NS;
        const marker = element(
          "span",
          `badge badge-xs ${recent ? "badge-info" : "badge-ghost"}`,
          `${recent ? "Fishing" : "Fishing history"} · ${fishing.retainedEvents}`,
        );
        marker.dataset.fishyTooltip = `${fishing.retainedEvents} retained fishing event${fishing.retainedEvents === 1 ? "" : "s"}; latest ${formatObservedTime(fishing.latestSourceObservedAtNs)}`;
        markers.append(marker);
      }
      const confidence = IDENTITY_CONFIDENCE[character.identityConfidence];
      if (confidence) {
        const marker = element(
          "span",
          `badge badge-xs ${confidence.className}`,
          confidence.label,
        );
        marker.dataset.fishyTooltip = confidence.detail;
        markers.append(marker);
      }
      button.append(markers);
      rail.append(button);
    }
    this.querySelector("#fishy-character-rail").replaceWith(rail);
    const historicalControl = this.querySelector("[data-historical-character-control]");
    historicalControl.hidden = historicalCharacters.length === 0;
    if (historicalCharacters.length > 0) {
      const toggle = element(
        "button",
        "btn btn-ghost btn-xs",
        this.#showHistoricalCharacters
          ? "Hide historical Characters"
          : `Show ${historicalCharacters.length} historical ${historicalCharacters.length === 1 ? "Character" : "Characters"}`,
      );
      toggle.type = "button";
      toggle.dataset.toggleHistoricalCharacters = "";
      toggle.setAttribute("aria-expanded", String(this.#showHistoricalCharacters));
      historicalControl.replaceChildren(toggle);
    } else {
      historicalControl.replaceChildren();
    }
    const inactiveControl = this.querySelector("[data-inactive-character-control]");
    inactiveControl.hidden = inactiveCharacters.length === 0;
    if (inactiveCharacters.length > 0) {
      const toggle = element(
        "button",
        "btn btn-ghost btn-xs",
        this.#showInactiveCharacters
          ? "Hide Characters without activity"
          : `Show ${inactiveCharacters.length} Character${inactiveCharacters.length === 1 ? "" : "s"} without activity`,
      );
      toggle.type = "button";
      toggle.dataset.toggleInactiveCharacters = "";
      toggle.setAttribute("aria-expanded", String(this.#showInactiveCharacters));
      inactiveControl.replaceChildren(toggle);
    } else {
      inactiveControl.replaceChildren();
    }

    const selected = data.filters.characters.find((entry) => entry.value === selectedValue);
    this.querySelector("[data-selected-character-name]").textContent = selected?.label ?? "All Characters";
    let selectedFamily = "Across all retained Families";
    if (selected) selectedFamily = familyByValue.get(selected.familyValue)?.label ?? "Family unavailable";
    if (selected?.identityState === "unresolvedActivity") selectedFamily = "Not assigned to a Character";
    this.querySelector("[data-selected-character-family]").textContent = selectedFamily;
    const selectedActivities = selected?.activitySummaries ?? allActivitySummaries;
    const hasFishing = selectedActivities.some((summary) => summary.activity === "fishing");
    this.querySelector('[data-activity="fishing"]').hidden = !hasFishing;
    this.querySelector("[data-fishing-workspace]").hidden = !hasFishing;
    this.querySelector("[data-no-character-activity]").hidden = hasFishing;
  }

  #renderCharacterSelection(selectedValue) {
    if (!this.#model) return;
    const data = validateCatchLogSlice(this.#model);
    this.querySelectorAll("[data-character-value]").forEach((button) => {
      const selected = button.dataset.characterValue === selectedValue;
      button.classList.toggle("btn-primary", selected);
      button.classList.toggle("btn-ghost", !selected);
      button.classList.toggle("border", !selected);
      button.classList.toggle("border-base-300", !selected);
      button.setAttribute("aria-selected", String(selected));
    });
    const familyByValue = new Map(data.filters.families.map((entry) => [entry.value, entry]));
    const selected = data.filters.characters.find((entry) => entry.value === selectedValue);
    this.querySelector("[data-selected-character-name]").textContent = selected?.label ?? "All Characters";
    let family = "Across all retained Families";
    if (selected) family = familyByValue.get(selected.familyValue)?.label ?? "Family unavailable";
    if (selected?.identityState === "unresolvedActivity") family = "Not assigned to a Character";
    this.querySelector("[data-selected-character-family]").textContent = family;
    const selectedActivities = selected?.activitySummaries
      ?? combinedActivitySummaries(data.filters.characters);
    const hasFishing = selectedActivities.some((summary) => summary.activity === "fishing");
    this.querySelector('[data-activity="fishing"]').hidden = !hasFishing;
    this.querySelector("[data-fishing-workspace]").hidden = !hasFishing;
    this.querySelector("[data-no-character-activity]").hidden = hasFishing;
    const selectedData = selectedCharacterData(data, selectedValue);
    this.#renderOverview(selectedData);
    this.#renderStream(selectedData);
    this.#renderRates(selectedData);
    this.#renderGroups(selectedData);
    this.#renderLensSelection();
  }

  #renderOverview(data) {
    const nowNs = BigInt(Date.now()) * 1_000_000n;
    const recentRows = data.rows.filter((row) => {
      const age = nowNs - BigInt(row.sourceObservedAtNs);
      return age >= 0n && age <= TWELVE_HOURS_NS;
    });
    const recentCatches = recentRows.filter((row) => row.kind === "fishingCatch");
    const recentCharacters = new Set(recentRows.map((row) => row.characterFilterKey)).size;
    const root = element("div", "grid gap-4");
    root.id = "fishy-fishing-overview";
    root.dataset.lensPanel = "overview";
    root.setAttribute("role", "tabpanel");
    const stats = element("div", "stats stats-vertical w-full border border-base-300 bg-base-100 sm:stats-horizontal");
    stats.append(
      stat("Catches · ~12h", recentCatches.length, "From the currently loaded bounded slice"),
      stat("Activity rows · ~12h", recentRows.length, `${data.statistics.eventCount} rows loaded in the selected slice`),
      stat("Characters · ~12h", recentCharacters, "Distinct Characters in those recent rows"),
    );
    root.append(stats);
    const lower = element("div", "grid gap-4 lg:grid-cols-[minmax(0,1.35fr)_minmax(16rem,0.65fr)]");
    const latest = recentRows.slice(0, 5);
    lower.append(
      listCard(latest, "No recent fishing activity", "No selected activity occurred in the last twelve hours of this bounded slice.", (row, rowIndex) => {
        const item = element("li", "list-row grid grid-cols-[4.75rem_minmax(0,1fr)] items-start gap-3 p-3 sm:grid-cols-[5.5rem_minmax(0,1fr)] sm:p-4");
        const content = element("div", "grid min-w-0 gap-1.5");
        content.append(eventMeta(row), eventBubble(row));
        item.append(
          eventIdentity(
            row,
            data.filters,
            rowIndex,
            this.#localPortraits.get(row.characterFilterKey),
          ),
          content,
        );
        return item;
      }),
      this.#statusCard(data),
    );
    root.append(lower);
    this.querySelector("#fishy-fishing-overview").replaceWith(root);
  }

  #statusCard(data) {
    const status = this.#sourceStatus ?? { label: "Connecting", detail: "Opening the activity source." };
    const root = element("div", "rounded-box border border-base-300 bg-base-200/25 p-4");
    root.append(
      element("p", "text-xs font-semibold uppercase tracking-[0.14em] text-base-content/50", "Data source"),
      element("p", "mt-2 font-semibold", status.label),
      element("p", "mt-1 text-sm leading-5 text-base-content/60", status.detail),
      element("p", "mt-3 text-xs text-base-content/50", `${data.candidateRows} bounded candidates · ${formatDuration(data.statistics.firstSourceObservedAtNs, data.statistics.lastSourceObservedAtNs)}`),
    );
    return root;
  }

  #renderStream(data) {
    const root = element("div");
    root.id = "fishy-catch-log-stream";
    root.append(listCard(data.rows, "No matching events", "Adjust the event-log filters or start the Fishing Logger to populate this Character.", (row, rowIndex) => {
      const item = element("li", "list-row grid grid-cols-[4.75rem_minmax(0,1fr)] items-start gap-3 p-3 sm:grid-cols-[5.5rem_minmax(0,1fr)] sm:p-4");
      const content = element("div", "grid min-w-0 gap-1.5");
      content.append(eventMeta(row), eventBubble(row));
      const canAssignIdentity = this.getAttribute("source") === "desktop"
        && row.localSourceSequence !== null
        && row.kind === "fishingCatch"
        && !["sourceObserved", "userConfirmed"].includes(row.identityConfidence);
      if (canAssignIdentity) {
        const assign = element(
          "button",
          "btn btn-ghost btn-xs shrink-0",
          row.identityConfidence === "unresolved" ? "Assign Character…" : "Correct Character…",
        );
        assign.type = "button";
        assign.dataset.identityAnchorSequence = row.localSourceSequence;
        assign.dataset.identityAnchorCharacter = row.characterLabel;
        content.append(assign);
      }
      item.append(
        eventIdentity(
          row,
          data.filters,
          rowIndex,
          this.#localPortraits.get(row.characterFilterKey),
        ),
        content,
      );
      return item;
    }));
    this.querySelector("#fishy-catch-log-stream").replaceWith(root);
  }

  #renderRates(data) {
    const root = element("div", "grid gap-3");
    root.id = "fishy-catch-log-rates";
    root.dataset.lensPanel = "rates";
    root.setAttribute("role", "tabpanel");
    root.append(element("p", "text-sm leading-6 text-base-content/65", `Occurrence uses ${data.statistics.eligibleCatchCount} composition-eligible catches as its denominator. It is descriptive evidence from the selected bounded slice, not a controlled fishing-table probability.`));
    if (!data.outcomes.length) {
      root.append(emptyCard("No source-proven rates", "The selected catches do not contain composition-eligible, source-proven outcomes."));
    } else {
      const wrapper = element("div", "max-w-full overflow-x-auto rounded-box border border-base-300");
      const table = element("table", "table table-sm");
      const head = element("thead");
      const headings = element("tr");
      ["Outcome", "Occurrence", "Catches", "Proven rows", "Quantity"].forEach((label) => headings.append(element("th", "", label)));
      head.append(headings);
      const body = element("tbody");
      data.outcomes.forEach((outcome) => {
        const row = element("tr");
        row.append(
          element("th", "font-semibold", outcome.label),
          element("td", "font-mono font-bold tabular-nums", percentage(outcome.catchOccurrences, data.statistics.eligibleCatchCount)),
          element("td", "tabular-nums", outcome.catchOccurrences),
          element("td", "tabular-nums", outcome.outcomeObservations),
          element("td", "tabular-nums", `×${outcome.quantityDecimal}`),
        );
        body.append(row);
      });
      table.append(head, body);
      wrapper.append(table);
      root.append(wrapper);
    }
    this.querySelector("#fishy-catch-log-rates").replaceWith(root);
  }

  #renderGroups(data) {
    const root = element("div", "grid gap-3");
    root.id = "fishy-catch-log-groups";
    root.dataset.lensPanel = "groups";
    root.setAttribute("role", "tabpanel");
    root.append(element("p", "text-sm leading-6 text-base-content/65", "Pairs show source-proven outcomes observed in the same catch. They are discovery evidence only; shared delivery or inventory changes do not create a link."));
    root.append(listCard(data.cooccurrences, "No same-catch groups yet", "At least one selected catch with two source-proven outcomes is required.", (pair) => {
      const item = element("li", "list-row items-center gap-3");
      item.append(
        element("div", "list-col-grow font-medium", `${pair.leftLabel} + ${pair.rightLabel}`),
        element("span", "badge badge-neutral tabular-nums", `${pair.catchCount} catches`),
      );
      return item;
    }));
    this.querySelector("#fishy-catch-log-groups").replaceWith(root);
  }

  #renderSourceStatus() {
    const status = this.#sourceStatus;
    if (!status) return;
    const dot = this.querySelector("[data-catch-log-source-dot]");
    if (dot) dot.className = SOURCE_STATUS_CLASS[status.state];
    const label = this.querySelector("[data-catch-log-source-label]");
    if (label) label.textContent = status.label;
  }

  #renderLensSelection() {
    this.querySelectorAll("[data-lens]").forEach((tab) => {
      const active = tab.dataset.lens === this.#lens;
      tab.classList.toggle("tab-active", active);
      tab.setAttribute("aria-selected", String(active));
    });
    this.querySelectorAll("[data-lens-panel]").forEach((panel) => panel.classList.toggle("hidden", panel.dataset.lensPanel !== this.#lens));
  }
}

if (!customElements.get("fishy-catch-log-view")) customElements.define("fishy-catch-log-view", FishyCatchLogView);

export {
  IDENTITY_ANCHOR_EVENT as CATCH_LOG_IDENTITY_ANCHOR_EVENT,
  QUERY_EVENT as CATCH_LOG_QUERY_EVENT,
};
